import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { submitSandboxRequest, submitContactRequest } from "../lib/api";
import { getWallet, shortFingerprint } from "../lib/wallet";
import { NEXT_STEPS } from "../lib/content";
import { toast } from "sonner";

const INTENT_OPTIONS = [
  { id: "private_sandbox", label: NEXT_STEPS.private_sandbox.label },
  { id: "pilot_scoping", label: NEXT_STEPS.pilot_scoping.label },
  { id: "public_demo", label: NEXT_STEPS.public_demo.label },
];

export const SandboxRequest = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setWallet(getWallet());
  }, []);

  const initialIntent = params.get("intent") || "private_sandbox";
  const draftId = params.get("draft");
  const [form, setForm] = useState({
    intent: initialIntent,
    name: "",
    role: "",
    organisation: "",
    contact_email: "",
    notes: "",
  });

  const intentMeta = useMemo(() => {
    if (form.intent === "pilot_scoping") return NEXT_STEPS.pilot_scoping;
    if (form.intent === "public_demo") return NEXT_STEPS.public_demo;
    return NEXT_STEPS.private_sandbox;
  }, [form.intent]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_email || !form.name) {
      toast.error("Name and contact email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        wallet_fingerprint: wallet ? wallet.fingerprint : null,
        wallet_handle: wallet ? wallet.handle : null,
        draft_id: draftId,
        sector: params.get("sector"),
        problem: params.get("problem"),
      };
      if (form.intent === "pilot_scoping") {
        await submitContactRequest(payload);
      } else {
        await submitSandboxRequest(payload);
      }
      setSubmitted(true);
      toast.success("Request received. We will respond on the email provided.");
    } catch (e) {
      toast.error("Could not submit request. Backend unavailable.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="sandbox-success" className="bg-white">
        <section className="border-b border-border">
          <div className="mx-auto max-w-[860px] px-6 py-24 text-center lg:px-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-800">
              Request received
            </p>
            <h1 className="mt-6 font-serif text-5xl font-light leading-tight tracking-tight">
              Acknowledged. Receipt issued.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              v1 issues a synthetic receipt only. A KATLAS-managed deployment
              would issue a signed receipt against your wallet fingerprint.
            </p>
            {wallet && (
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground">
                bound to · {shortFingerprint(wallet.fingerprint)}
              </p>
            )}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Button
                asChild
                data-testid="sandbox-success-back-home"
                className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
              >
                <Link to="/">Return to overview</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="sandbox-success-wallet"
                className="rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
              >
                <Link to="/wallet">Open wallet</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="sandbox-request-page" className="bg-white">
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1280px] gap-12 px-6 py-16 md:grid-cols-12 md:py-20 lg:px-10">
          <div className="md:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Next step · {intentMeta.label.toLowerCase()}
            </p>
            <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              Open a private path forward.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              {intentMeta.summary}
            </p>
            <ul className="mt-10 space-y-3 font-mono text-[11px] uppercase tracking-[0.16em] text-foreground">
              <li>› Synthetic-first by default</li>
              <li>› No production data required</li>
              <li>› v1 receipts are simulated</li>
              <li>› Migration path to KATLAS-owned trust boundary</li>
            </ul>

            <div className="mt-10 hidden border border-border bg-[#FAFAFA] p-6 md:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                What we retain
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground">
                Your name, contact email, and selections — only enough to
                respond. No broad customer secrets. No persistent profile.
              </p>
            </div>
          </div>
          <div className="md:col-span-7">
            <form
              data-testid="sandbox-form"
              onSubmit={handleSubmit}
              className="border border-border bg-white p-8 md:p-10"
            >
              <div className="grid gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label
                    htmlFor="sandbox-intent"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Intent
                  </Label>
                  <Select
                    value={form.intent}
                    onValueChange={(v) => update("intent", v)}
                  >
                    <SelectTrigger
                      id="sandbox-intent"
                      data-testid="sandbox-intent-trigger"
                      className="mt-2 h-11 rounded-none border-foreground/30"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INTENT_OPTIONS.map((o) => (
                        <SelectItem
                          key={o.id}
                          value={o.id}
                          data-testid={`sandbox-intent-${o.id}`}
                        >
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label
                    htmlFor="sandbox-name"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Name *
                  </Label>
                  <Input
                    id="sandbox-name"
                    data-testid="sandbox-name"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="mt-2 h-11 rounded-none border-foreground/30"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="sandbox-email"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Contact email *
                  </Label>
                  <Input
                    id="sandbox-email"
                    type="email"
                    data-testid="sandbox-email"
                    required
                    value={form.contact_email}
                    onChange={(e) => update("contact_email", e.target.value)}
                    className="mt-2 h-11 rounded-none border-foreground/30"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="sandbox-role"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Role
                  </Label>
                  <Input
                    id="sandbox-role"
                    data-testid="sandbox-role"
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    className="mt-2 h-11 rounded-none border-foreground/30"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="sandbox-org"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Organisation
                  </Label>
                  <Input
                    id="sandbox-org"
                    data-testid="sandbox-org"
                    value={form.organisation}
                    onChange={(e) => update("organisation", e.target.value)}
                    className="mt-2 h-11 rounded-none border-foreground/30"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor="sandbox-notes"
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    Anything specific to scope
                  </Label>
                  <Textarea
                    id="sandbox-notes"
                    data-testid="sandbox-notes"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Optional. Synthetic context only."
                    className="mt-2 rounded-none border-foreground/30"
                  />
                </div>
              </div>

              {wallet && (
                <p
                  data-testid="sandbox-wallet-line"
                  className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Will be bound to wallet · {shortFingerprint(wallet.fingerprint)}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                >
                  ← Back
                </button>
                <Button
                  type="submit"
                  data-testid="sandbox-submit"
                  disabled={submitting}
                  className="rounded-none px-6 font-mono text-xs uppercase tracking-[0.18em]"
                >
                  {submitting ? "Submitting…" : "Submit request →"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SandboxRequest;
